import React, { useState, useEffect, useMemo, useCallback } from 'react'
import B2BLayout from '../../../../layouts/b2b'
import { Button, Table, Modal } from 'antd'
import { getColumns } from './columns'
import fetchAPI from '../../../../../lib/utils/fetch-api'
import { useNavigate } from 'react-router-dom'
import { MapTitle } from '../../../appeals/card/styles'
import { PlusOutlined } from '@ant-design/icons'
import { TablePaginationConfig } from 'antd/es/table'
import { Sorter } from '../../../../interfaces'
import { objectToUrl } from '../../../utils'
import { DataType } from './interface'
import PlanModal from '../modal'

const HRPlansList = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<DataType[]>([])
  const [loading, setLoading] = useState(false)
  const [sorter, setSorter] = useState<Sorter>({})
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DataType | null>(null)
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 10,
    position: ['bottomCenter'],
    showSizeChanger: false,
  })

  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(
    () => [
      {
        href: '/admin',
        title: 'Админка',
      },
      {
        href: '/hr/dashboard',
        title: 'Дашборд',
      },
      {
        title: 'Планы',
      },
    ],
    [],
  )

  useEffect(() => {
    try {
      /* Пытаюсь получить список */
      getData()
    } catch (e) {
      alert('Не удалось получить данные, пожалуйста, перезагрузите страницу')
    }
  }, [])

  /**
   * Обновляет состояние пагинации
   */
  const updatePagination = useCallback(
    newState => {
      setPagination({
        ...pagination,
        ...newState,
      })
    },
    [pagination, setPagination],
  )

  /**
   * Получает записи
   */
  const getData = useCallback(() => {
    try {
      setLoading(true)
      const currentUrlParams = Object.fromEntries(new URLSearchParams(window.location.search))
      fetchAPI(`/api/hr/plans${objectToUrl(currentUrlParams)}`).then(
        ({ data, pagination, sorter }) => {
          if (data) {
            setData(
              data.map(item => ({
                ...item,
              })),
            )
          }
          if (pagination) {
            updatePagination(pagination)
          }
          if (sorter) {
            setSorter(sorter)
          }
          setLoading(false)
        },
      )
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
      setLoading(false)
    }
  }, [updatePagination])

  /**
   * Отслеживает изменение состояния таблицы
   *
   * @param pagination
   * @param _filters
   * @param sorter
   */
  const handleTableChange = (pagination, _filters, sorter) => {
    const currentUrlParams = Object.fromEntries(new URLSearchParams(window.location.search))
    const current = {
      ...currentUrlParams,
      sort_order: sorter?.order,
      sort_field: sorter?.field,
      page: pagination.current,
    }
    if (current?.page === 1) {
      delete current.page
    }
    if (current?.sort_order === undefined) {
      delete current.sort_order
      delete current.sort_field
    }
    const urlParams = new URLSearchParams(current).toString()
    navigate({ search: urlParams })
    getData()
  }

  const handleCreate = () => {
    setEditingRecord(null)
    setModalVisible(true)
  }

  const handleEdit = (record: DataType) => {
    setEditingRecord(record)
    setModalVisible(true)
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    setEditingRecord(null)
  }

  const handleModalOk = () => {
    setModalVisible(false)
    setEditingRecord(null)
    getData()
  }

  const handleStop = (record: DataType) => {
    Modal.confirm({
      title: 'Вы уверены, что хотите остановить этот план?',
      okText: 'Да',
      okType: 'danger',
      cancelText: 'Нет',
      onOk: async () => {
        try {
          await fetchAPI(`/api/hr/plans/${record.id}`, {
            method: 'POST',
            body: JSON.stringify({ status: 'stopped' }),
          })
          getData()
        } catch (error) {
          console.error('Ошибка при остановке плана:', error)
        }
      },
    })
  }

  const handleDelete = (record: DataType) => {
    Modal.confirm({
      title: 'Вы уверены, что хотите удалить эту запись?',
      okText: 'Да',
      okType: 'danger',
      cancelText: 'Нет',
      onOk: async () => {
        try {
          await fetchAPI(`/api/hr/plans/${record.id}`, { method: 'DELETE' })
          getData()
        } catch (error) {
          console.error('Ошибка при удалении плана:', error)
        }
      },
    })
  }

  const handleEditWrapper = useCallback(
    (record: DataType) => {
      handleEdit(record)
    },
    [],
  )

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <MapTitle>Планы</MapTitle>
        <Button onClick={handleCreate}>
          <PlusOutlined /> Добавить
        </Button>
      </div>
      <Table
        showSorterTooltip={{ overlay: 'Нажмите для сортировки', placement: 'bottom' }}
        columns={getColumns(sorter, handleStop, handleEditWrapper, handleDelete)}
        rowKey={r => r?.id}
        dataSource={data}
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        scroll={{
          x: 'max-content',
          y: '72vh',
        }}
        size="small"
      />
      <PlanModal
        visible={modalVisible}
        record={editingRecord}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
      />
    </B2BLayout>
  )
}

export default HRPlansList
